import { zodResolver } from '@hookform/resolvers/zod';
import type { Tenant } from '@packages/shared-types';
import { Pencil, Plus, ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { TableActionsMenu, type TableAction } from '@/components/shared/TableActionsMenu';
import {
  createTenant,
  getTenantsPaginated,
  type CreateTenantPayload,
} from '@/services/tenantService';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

const tenantFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  subdomain: z.string().optional(),
});

type TenantFormValues = z.infer<typeof tenantFormSchema>;

export function TenantsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchToTenant } = useTenantSwitcher();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [switchMessage, setSwitchMessage] = useState<string | null>(null);

  const pagination = usePaginationFromUrl();

  const loadTenants = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTenantsPaginated({
        page: pagination.page,
        limit: pagination.limit,
      });
      setTenants(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('pages.tenants.error.load'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [t, pagination.page, pagination.limit]);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (!switchMessage) return undefined;
    const timer = window.setTimeout(() => setSwitchMessage(null), 4000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [switchMessage]);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      name: '',
      subdomain: '',
    },
  });

  const handleCreate = async (values: TenantFormValues): Promise<void> => {
    try {
      const payload: CreateTenantPayload = {
        name: values.name,
        subdomain: values.subdomain || undefined,
      };
      const newTenant = await createTenant(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      // Navigate to tenant edit page
      navigate(`/tenants/${newTenant.id}/edit`);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('pages.tenants.error.create'),
      );
    }
  };


  const handleSwitchTenant = useCallback(
    (tenant: Tenant) => {
      switchToTenant(tenant);
      setSwitchMessage(t('pages.tenants.switch.success', { name: tenant.name }));
      navigate('/dashboard');
    },
    [switchToTenant, t, navigate],
  );

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleEditClick = useCallback(
    (tenantId: string) => {
      return () => navigate(`/tenants/${tenantId}/edit`);
    },
    [navigate],
  );

  const handleSwitchClick = useCallback(
    (tenant: Tenant) => {
      return () => handleSwitchTenant(tenant);
    },
    [handleSwitchTenant],
  );

  const handleCreateButtonClick = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('pages.tenants.title')}
        description={t('pages.tenants.description')}
        actions={
          <Button onClick={handleCreateButtonClick}>
            <Plus className="h-4 w-4" />
            {t('pages.tenants.actions.create')}
          </Button>
        }
      />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {switchMessage && (
        <Card className="border-primary bg-primary/10">
          <CardContent className="p-4">
            <p className="text-sm text-primary-foreground">{switchMessage}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{t('pages.tenants.loading')}</span>
          </div>
        ) : tenants.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('pages.tenants.empty')}
          </div>
        ) : (
          <>
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]"></TableHead>
                  <TableHead>{t('pages.tenants.list.name')}</TableHead>
                  <TableHead>{t('pages.tenants.list.subdomain')}</TableHead>
                  <TableHead>{t('pages.tenants.list.createdAt')}</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => {
                  const actions: TableAction[] = [
                    {
                      label: t('pages.tenants.actions.edit'),
                      icon: Pencil,
                      onClick: handleEditClick(tenant.id),
                    },
                    {
                      label: t('pages.tenants.actions.switch'),
                      icon: ArrowRight,
                      onClick: handleSwitchClick(tenant),
                    },
                  ];
                  return (
                    <TableRow
                      key={tenant.id}
                      onRowClick={handleSwitchClick(tenant)}
                    >
                    <TableCell>
                      <Avatar className="h-10 w-10">
                        {tenant.profileImage ? (
                          <AvatarImage
                            src={tenant.profileImage}
                            alt={tenant.name}
                          />
                        ) : null}
                        <AvatarFallback className="text-xs">
                          {tenant.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell>
                      {tenant.subdomain || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(tenant.createdAt)}</TableCell>
                    <TableCell>
                      <TableActionsMenu actions={actions} />
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
            pageOfLabel={t('pages.common.pagination.pageOf', {
              current: pagination.page,
              total: pagination.totalPages,
            })}
            onRefresh={() => void loadTenants()}
            isRefreshing={isLoading}
          />
          </>
        )}
      </TableCard>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pages.tenants.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('pages.tenants.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.tenants.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.tenants.form.namePlaceholder')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {createErrors.name.message}
                  </p>
                )}
              </div>
              {/* Subdomain field hidden for now */}
              {/* <div className="space-y-2">
                <Label htmlFor="create-subdomain">
                  {t('pages.tenants.form.subdomain')}
                </Label>
                <Input
                  id="create-subdomain"
                  {...registerCreate('subdomain')}
                  placeholder={t('pages.tenants.form.subdomainPlaceholder')}
                />
                {createErrors.subdomain && (
                  <p className="text-sm text-destructive">
                    {createErrors.subdomain.message}
                  </p>
                )}
              </div> */}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.tenants.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating
                  ? t('pages.tenants.actions.creating')
                  : t('pages.tenants.actions.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
